import { computed, makeObservable, observable, reaction, action, runInAction } from "mobx";
import { INoteObservable, NotesSortByKind } from "@/store/note";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import {
  ItemListItem,
  ItemListItemKind,
  ItemListItemRowData,
  ItemListCarouselRowData,
  ItemListRowData,
  ItemListRowType,
  ItemListSectionHeaderRowData,
} from "@/components/item-list/types";
import { actions } from "@/actions";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet/types";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { SearchEngineResponse } from "@/store/pages/SearchPageStore/types";
import { ApiSchema, api } from "@/modules/api";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { resolveFavoriteItemSyncModelUuid } from "@/modules/uuid/sync-models/resolveFavoriteItemSyncModelUuid";
import { resolveSavedSearchSyncModelUuid } from "@/modules/uuid/sync-models/resolveSavedSearchSyncModelUuid";
import { AddToCollectionModalStore } from "@/components/modal/add-to-collection";
import { ItemPreviewState } from "@/components/item-preview/item-preview-state";
import { ShareSheetModalStore } from "@/components/modal/share-sheet/share-sheet-modal-store";
import { getItemListCarouselItemForCollection } from "@/components/item-list/helpers/get-item-list-carousel-item-for-collection";
import { RemoveSavedSearchFromFavoritesOperation } from "@/store/sync/operations/favorites/RemoveSavedSearchFromFavoritesOperation";
import { AddSavedSearchToFavoritesOperation } from "@/store/sync/operations/favorites/AddSavedSearchToFavoritesOperation";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { SearchResultVotingButtons } from "@/store/pages/SearchPageStore/searchResultVotingButtons";
import { PaginatableApiQueryObservable } from "@/store/queries/common/PaginatableApiQueryObservable";
import { SearchEngineResult } from "@/modules/url-params/search-engine-params/types";
import {
  createSearchEngineApiQueryObservable,
  SearchEngineKind,
} from "@/store/queries/search/createSearchEngineApiQueryObservable";
import SearchResultDebugInfo from "@/store/pages/SearchPageStore/searchResultDebugInfo";
import { EventContext } from "@/domains/metrics/context";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { SearchPageFilter } from "@/modules/filters/search/SearchPageFilter";
import { generateShortDateString } from "@/domains/date/date";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { searchCollections } from "@/store/collections/CollectionSearch";
import { CollectionSearchReturnType, CollectionSearchSortByKind } from "@/store/collections/types";
import { SearchPagePaginationCapacityReached } from "@/pages/search/components/search-page-pagination-capacity-reached";
import { MDSIconColor, MDSIconKind, MDSMenuContentItemKind, MDSMenuContentList } from "@/mds";
import { To } from "react-router";
import { itemsModule } from "@/modules/items";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import { buildProxiedAssetUrl, resolveMatchingAttachmentByUrl } from "@/modules/asset-proxy";
import { assetProxyModule, noteModule } from "@mem-labs/common-editor";
import { IImageLightboxImage } from "@/components/note/media-viewer/image/image-lightbox.types";
import {
  SearchResultAttachmentMediaOrder,
  SearchResultImageThumbnailItem,
  SearchResultPdfChipItem,
  SearchResultPdfChips,
} from "@/pages/search/components/search-result-pdf-chips";
import { SearchResultExtraRowContent } from "@/store/pages/SearchPageStore/SearchResultExtraRowContent";
import { resolveSearchResultFeaturedSnippet } from "@/store/pages/SearchPageStore/resolveSearchResultFeaturedSnippet";
import { selectSearchResultItems } from "./select-search-result-items";

export interface ISearchPageStore {
  isSuccess: boolean;
  isLoading: boolean;
  hasNoItems: boolean;
  itemRows: ItemListRowData[];
  filters: SearchPageFilter;
  listState: {
    useListStateEffects: () => void;
    selectedItemIds: string[];
  };
  deepSearchResults: SearchEngineResult[];
  showDeepSearchResults: boolean;
  addToCollectionModal: { selectedNoteIds: string[] };
  pdfLightboxState?: SearchPagePdfLightboxState | null;
  imageLightboxState?: SearchPageImageLightboxState | null;
  closePdfLightbox?: () => void;
  closeImageLightbox?: () => void;
}

// SearchPage can be rendered from the home page too when facet filters are applied.

export class SearchPageStore extends AppSubStore implements ISearchPageStore {
  tabId: string;
  supportsAddToCollectionListAction = true;
  supportsMoveToTrashListAction = true;
  supportsMoveNoteToTrashListAction = true;
  supportsChatListAction = true;
  listState: ListStateObservable;
  addToCollectionModal: AddToCollectionModalStore;
  shareSheetModal: ShareSheetModalStore;
  apiQuery: PaginatableApiQueryObservable<SearchEngineResponse, SearchEngineResult>;
  fastApiQuery: PaginatableApiQueryObservable<SearchEngineResponse, SearchEngineResult>;
  previousSearchEngineQueryString: string | null = null;
  filters: SearchPageFilter;
  eventContext = EventContext.SearchMultiselectActions;
  showDeepSearchResults = true;
  pdfLightboxState: SearchPagePdfLightboxState | null = null;
  imageLightboxState: SearchPageImageLightboxState | null = null;

  items: SearchEngineResult[] = [];
  deepSearchItems: SearchEngineResult[] = [];

  static MAX_ITEMS_COUNT = 300;

  // Deduping, sorting, mapping can take some time, so we need to account that in "loader" state.
  private isDedupingItems = false;
  private prefetchedDeepSearchNoteContentIds = new Set<string>();

  /** Prevents an older provisional FAST setup from overwriting a newer SMART setup. */
  private itemSetupGeneration = 0;

  constructor(injectedDeps: AppSubStoreArgs & { path?: To; tabId: string }) {
    super(injectedDeps);

    this.tabId = injectedDeps.tabId;
    this.addToCollectionModal = new AddToCollectionModalStore(injectedDeps, {});
    this.shareSheetModal = ShareSheetModalStore.forAppStore({ appStore: this.store });
    this.listState = new ListStateObservable({ ...injectedDeps, listStateProvider: this });
    this.filters = new SearchPageFilter(this.store, injectedDeps.path);

    this.fastApiQuery = this.createApiQueryObservable(SearchEngineKind.FAST);
    this.apiQuery = this.createApiQueryObservable(SearchEngineKind.SMART);

    makeObservable<
      this,
      | "generateNoteDropdown"
      | "generateCollectionDropdown"
      | "generateItemRows"
      | "submitClickFeedback"
      | "isDedupingItems"
      | "_shouldSkipRefresh"
      | "resolveDeepSearchAttachmentMatches"
      | "resolveDeepSearchPdfAttachmentMatches"
      | "resolveDeepSearchImageAttachmentMatches"
      | "resolveDeepSearchPdfChipItems"
      | "resolveDeepSearchImageThumbnailItems"
      | "openPdfLightbox"
      | "openDeepSearchPdfLightbox"
      | "openImageLightbox"
      | "openDeepSearchImageLightbox"
      | "resolveProxiedAttachmentUrl"
      | "resolveAttachmentUrlFromNoteContent"
      | "resolveAttachmentsFromLoadedNoteContent"
      | "resolveFileAttachmentsFromLoadedNoteContent"
      | "resolveImageAttachmentsFromLoadedNoteContent"
      | "resolveMatchingFileAttachment"
      | "resolveMatchingImageAttachment"
      | "mergeDuplicateNoteSearchResultItem"
      | "prefetchDeepSearchNoteContentDocuments"
      | "prefetchedDeepSearchNoteContentIds"
      | "itemSetupGeneration"
    >(this, {
      supportsChatListAction: true,
      tabId: true,
      refreshSearchEngineQuery: true,
      loadMoreFastSearchItems: true,
      sortAndGroupNotes: true,
      sortAndGroupCollections: true,
      dedupeIdForItemValue: true,
      dedupeItemsForCarouselValue: true,
      submitClickFeedback: true,
      generateItemRows: true,
      resolveDeepSearchAttachmentMatches: false,
      resolveDeepSearchPdfAttachmentMatches: false,
      resolveDeepSearchImageAttachmentMatches: false,
      resolveDeepSearchPdfChipItems: false,
      resolveDeepSearchImageThumbnailItems: false,
      openPdfLightbox: false,
      openDeepSearchPdfLightbox: false,
      openImageLightbox: false,
      openDeepSearchImageLightbox: false,
      resolveProxiedAttachmentUrl: false,
      resolveAttachmentUrlFromNoteContent: false,
      resolveAttachmentsFromLoadedNoteContent: false,
      resolveFileAttachmentsFromLoadedNoteContent: false,
      resolveImageAttachmentsFromLoadedNoteContent: false,
      resolveMatchingFileAttachment: false,
      resolveMatchingImageAttachment: false,
      mergeDuplicateNoteSearchResultItem: false,
      prefetchDeepSearchNoteContentDocuments: false,
      prefetchedDeepSearchNoteContentIds: false,
      itemSetupGeneration: false,
      supportsAddToCollectionListAction: observable,
      supportsMoveToTrashListAction: observable,
      supportsMoveNoteToTrashListAction: false,

      addToCollectionModal: false,
      shareSheetModal: false,
      listState: false,
      eventContext: false,
      filters: true,

      // API Query
      previousSearchEngineQueryString: observable,
      resetPreviousQueryString: action,
      createApiQueryObservable: false,
      apiQuery: observable,
      load: false,
      isLoading: computed,
      isSuccess: computed,
      fastApiQuery: true,
      showDeepLoader: computed,

      _shouldSkipRefresh: false,

      // Item Rows
      itemRows: computed,
      generateNoteDropdown: false,
      generateCollectionDropdown: false,
      orderedItemIds: computed,
      hasNoItems: computed,
      showDeepSearchResults: observable,
      setShowDeepSearchResults: action,
      pdfLightboxState: observable,
      closePdfLightbox: action,
      imageLightboxState: observable,
      closeImageLightbox: action,
      deepSearchResults: computed,
      items: observable,
      setupIds: action,
      deepSearchItems: observable,
      isDedupingItems: observable,
      reachedCapacity: computed,
      notAllItemsLoadedButCould: computed,

      // Favorites
      id: computed,
      isFavorited: computed,
      favoriteItemId: computed,
      toggleFavorite: action,
    });

    reaction(
      () => this.filters.searchQuery,
      value => {
        const queryString = this.filters.params.queryString;
        if (!value || !queryString || value === this.previousSearchEngineQueryString) {
          return;
        }

        this.refreshSearchEngineQuery(value);
      },
      { fireImmediately: true }
    );

    reaction(
      () => this.filters.dateParams,
      () => this.refreshSearchEngineQuery(this.filters.searchQuery)
    );

    reaction(
      () => [this.apiQuery.isLoading, this.fastApiQuery.isLoading, this.showDeepSearchResults],
      () => {
        this.setupIds();
      }
    );

    reaction(
      () => this.apiQuery.data,
      data => {
        if (data) {
          this.store.debug.setLatestSmartSearchResponse(data);
        }
      }
    );

    reaction(
      () => this.fastApiQuery.data,
      data => {
        if (data) {
          this.store.debug.setLatestFastSearchResponse(data);
        }
      }
    );
  }

  refreshSearchEngineQuery(searchQuery: string) {
    this.apiQuery = this.createApiQueryObservable(SearchEngineKind.SMART);
    this.fastApiQuery = this.createApiQueryObservable(SearchEngineKind.FAST);
    this.fastApiQuery.fetchData();
    this.apiQuery.fetchData();
    this.previousSearchEngineQueryString = searchQuery;
    this.store.debug.setLatestSearchQuery(searchQuery);
    this.showDeepSearchResults = true;
    this.listState.clearSelection();
  }

  createApiQueryObservable(
    searchEngineKind: SearchEngineKind
  ): PaginatableApiQueryObservable<SearchEngineResponse, SearchEngineResult> {
    return createSearchEngineApiQueryObservable({
      searchEngineKind,
      getSpaceId: () => this.store.spaces.myPersonalSpaceId,
      searchParams: this.filters.params,
      dateParams: this.filters.dateParams,
      apiClient: api,
    });
  }

  load = () => {
    this.fastApiQuery.fetchData();
    this.apiQuery.fetchData();
  };

  get reachedCapacity() {
    return this.items.length >= SearchPageStore.MAX_ITEMS_COUNT;
  }

  get notAllItemsLoadedButCould() {
    return (
      this.reachedCapacity && (this.fastApiQuery.canFetchMoreData || this.apiQuery.canFetchMoreData)
    );
  }

  loadMoreFastSearchItems() {
    if (this.fastApiQuery.isLoading || this.fastApiQuery.latestRequest?.isError) {
      return;
    }

    this.fastApiQuery.fetchData();
  }

  get showDeepLoader() {
    return this.apiQuery.isLoading;
  }

  get isLoading() {
    return this.apiQuery.isLoading || this.fastApiQuery.isLoading;
  }

  get isSuccess() {
    return this.apiQuery.isSuccess || this.fastApiQuery.isSuccess;
  }

  setShowDeepSearchResults(value: boolean) {
    this.showDeepSearchResults = value;
  }

  get deepSearchResults() {
    return this.apiQuery.items
      .filter(item => item.kind === "ITEM" && item.value.kind === "NOTE")
      .map(item => ({
        ...item,
        isDeepSearch: true,
      }));
  }

  closePdfLightbox = () => {
    this.pdfLightboxState = null;
  };

  closeImageLightbox = () => {
    this.imageLightboxState = null;
  };

  private async resolveProxiedAttachmentUrl({ attachmentUrl }: { attachmentUrl: string }) {
    await this.store.auth.assetAccessTokenState.ensureToken();

    const classification = classifyProxyAttachmentUrl({ attachmentUrl });
    if (classification.kind === "already-proxied") {
      return classification.url;
    }

    const proxiedUrl = buildProxiedAssetUrl({
      originalUrl: classification.originalUrl,
      assetAccessTokenState: this.store.auth.assetAccessTokenState,
    });

    return proxiedUrl ?? classification.originalUrl;
  }

  private async openPdfLightbox({
    attachmentUrl,
    fileName,
    initialPage,
  }: {
    attachmentUrl: string;
    fileName: string;
    initialPage?: number;
  }) {
    const proxiedUrl = await this.resolveProxiedAttachmentUrl({ attachmentUrl });

    runInAction(() => {
      this.pdfLightboxState = {
        src: proxiedUrl ?? attachmentUrl,
        fileName,
        initialPage,
      };
    });
  }

  private resolveDeepSearchAttachmentMatches({
    noteValue,
    attachmentType,
  }: {
    noteValue: SearchEngineResultItemNoteValue;
    attachmentType?: SearchEngineResultAttachmentType;
  }): SearchEngineResultAttachmentMatchInfo[] {
    const attachmentMatchInfos = noteValue.attachment_match_infos;

    if (!attachmentMatchInfos?.length) {
      return [];
    }

    const matches: SearchEngineResultAttachmentMatchInfo[] = [];
    const seenAttachmentIds = new Set<string>();

    for (const attachmentMatchInfo of attachmentMatchInfos) {
      if (attachmentType && attachmentMatchInfo.attachment_type !== attachmentType) {
        continue;
      }

      if (
        !attachmentMatchInfo.attachment_id ||
        seenAttachmentIds.has(attachmentMatchInfo.attachment_id)
      ) {
        continue;
      }

      seenAttachmentIds.add(attachmentMatchInfo.attachment_id);
      matches.push(attachmentMatchInfo);
    }

    return matches;
  }

  private resolveDeepSearchPdfAttachmentMatches({
    noteValue,
  }: {
    noteValue: SearchEngineResultItemNoteValue;
  }): SearchEngineResultAttachmentMatchInfo[] {
    return this.resolveDeepSearchAttachmentMatches({
      noteValue,
      attachmentType: "pdf",
    });
  }

  private resolveDeepSearchImageAttachmentMatches({
    noteValue,
  }: {
    noteValue: SearchEngineResultItemNoteValue;
  }): SearchEngineResultAttachmentMatchInfo[] {
    return this.resolveDeepSearchAttachmentMatches({
      noteValue,
      attachmentType: "image",
    });
  }

  private resolveDeepSearchPdfChipItems({
    attachmentMatches,
    noteObservable,
    searchEngineResultId,
  }: {
    attachmentMatches: SearchEngineResultAttachmentMatchInfo[];
    noteObservable: INoteObservable;
    searchEngineResultId: string;
  }): SearchResultPdfChipItem[] {
    if (!attachmentMatches.length) {
      return [];
    }

    const fileAttachments = this.resolveFileAttachmentsFromLoadedNoteContent({
      noteObservable,
    });

    return attachmentMatches.map(attachmentMatchInfo => {
      const matchingFileAttachment = this.resolveMatchingFileAttachment({
        searchAttachmentUrl: attachmentMatchInfo.attachment_url,
        fileAttachments,
      });

      const fileName = matchingFileAttachment?.filename || attachmentMatchInfo.filename || "PDF";

      return {
        id: `${attachmentMatchInfo.attachment_id}-${attachmentMatchInfo.page_number ?? 0}`,
        label: fileName,
        fileSizeBytes: matchingFileAttachment?.sizeBytes ?? null,
        showSyncedStatus: false,
        onClick: () => {
          trackEvent(TrackedEvent.ViewNoteAttachmentPdf, {
            context: "search_result",
            search_engine_result_id: searchEngineResultId,
            note_id: noteObservable.id,
            attachment_id: attachmentMatchInfo.attachment_id,
          });

          void this.openDeepSearchPdfLightbox({
            noteObservable,
            attachmentMatchInfo,
            fallbackFileName: fileName,
          });
        },
      } satisfies SearchResultPdfChipItem;
    });
  }

  private resolveDeepSearchImageThumbnailItems({
    attachmentMatches,
    noteObservable,
  }: {
    attachmentMatches: SearchEngineResultAttachmentMatchInfo[];
    noteObservable: INoteObservable;
  }): SearchResultImageThumbnailItem[] {
    if (!attachmentMatches.length) {
      return [];
    }

    void this.store.auth.assetAccessTokenState.ensureToken();

    const imageAttachments = this.resolveImageAttachmentsFromLoadedNoteContent({
      noteObservable,
    });

    const lightboxItems: SearchPageImageLightboxItem[] = attachmentMatches
      .map(attachmentMatchInfo => {
        const matchingImageAttachment = this.resolveMatchingImageAttachment({
          searchAttachmentUrl: attachmentMatchInfo.attachment_url,
          imageAttachments,
        });

        const imageUrl = matchingImageAttachment?.url;
        if (!imageUrl) {
          return null;
        }

        return {
          id: attachmentMatchInfo.attachment_id,
          src: imageUrl,
          alt:
            matchingImageAttachment?.alt ||
            matchingImageAttachment?.name ||
            matchingImageAttachment?.filename ||
            attachmentMatchInfo.filename ||
            "Image attachment",
        } satisfies SearchPageImageLightboxItem;
      })
      .filter((item): item is SearchPageImageLightboxItem => !!item);

    return lightboxItems.map((lightboxItem, index) => ({
      id: lightboxItem.id,
      src:
        buildProxiedAssetUrl({
          originalUrl: lightboxItem.src,
          assetAccessTokenState: this.store.auth.assetAccessTokenState,
        }) ?? lightboxItem.src,
      alt: lightboxItem.alt,
      onClick: () => {
        void this.openDeepSearchImageLightbox({
          images: lightboxItems,
          initialIndex: index,
        });
      },
    }));
  }

  private async openImageLightbox({
    images,
    initialIndex,
  }: {
    images: SearchPageImageLightboxItem[];
    initialIndex: number;
  }) {
    if (!images.length || initialIndex < 0 || initialIndex >= images.length) {
      return;
    }

    const proxiedImages = await Promise.all(
      images.map(async image => ({
        src: await this.resolveProxiedAttachmentUrl({ attachmentUrl: image.src }),
        alt: image.alt,
      }))
    );

    runInAction(() => {
      this.imageLightboxState = {
        images: proxiedImages,
        initialIndex,
      };
    });
  }

  private async openDeepSearchImageLightbox({
    images,
    initialIndex,
  }: {
    images: SearchPageImageLightboxItem[];
    initialIndex: number;
  }) {
    await this.openImageLightbox({
      images,
      initialIndex,
    });
  }

  private async openDeepSearchPdfLightbox({
    noteObservable,
    attachmentMatchInfo,
    fallbackFileName,
  }: {
    noteObservable: INoteObservable;
    attachmentMatchInfo: SearchEngineResultAttachmentMatchInfo;
    fallbackFileName: string;
  }) {
    const fileAttachments = this.resolveFileAttachmentsFromLoadedNoteContent({
      noteObservable,
    });
    const matchingFileAttachment = this.resolveMatchingFileAttachment({
      searchAttachmentUrl: attachmentMatchInfo.attachment_url,
      fileAttachments,
    });

    const attachmentUrl =
      matchingFileAttachment?.url ||
      (await this.resolveAttachmentUrlFromNoteContent({
        noteObservable,
        searchAttachmentUrl: attachmentMatchInfo.attachment_url,
        attachmentType: "file",
      }));

    if (!attachmentUrl) {
      return;
    }

    const fileName =
      matchingFileAttachment?.filename || attachmentMatchInfo.filename || fallbackFileName;

    await this.openPdfLightbox({
      attachmentUrl,
      fileName,
      initialPage:
        typeof attachmentMatchInfo.page_number === "number"
          ? attachmentMatchInfo.page_number
          : undefined,
    });
  }

  private async resolveAttachmentUrlFromNoteContent({
    noteObservable,
    searchAttachmentUrl,
    attachmentType,
  }: {
    noteObservable: INoteObservable;
    searchAttachmentUrl?: string | null;
    attachmentType: SearchResultMediaAttachment["type"];
  }): Promise<string | null> {
    await noteObservable.fetchNoteContentDocument();
    if (attachmentType === "file") {
      const fileAttachments = this.resolveFileAttachmentsFromLoadedNoteContent({
        noteObservable,
      });
      const matchingFileAttachment = this.resolveMatchingFileAttachment({
        searchAttachmentUrl,
        fileAttachments,
      });

      return matchingFileAttachment?.url ?? null;
    }

    const imageAttachments = this.resolveImageAttachmentsFromLoadedNoteContent({
      noteObservable,
    });
    const matchingImageAttachment = this.resolveMatchingImageAttachment({
      searchAttachmentUrl,
      imageAttachments,
    });

    return matchingImageAttachment?.url ?? null;
  }

  private resolveAttachmentsFromLoadedNoteContent({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): SearchResultMediaAttachment[] {
    const encodedContent = noteObservable.remoteContent;

    if (!encodedContent) {
      return [];
    }

    const extractedContent = noteModule.extractContentFromEncodedContent({
      encodedContent,
    });

    return extractedContent.attachments.filter(
      (attachment): attachment is SearchResultMediaAttachment =>
        attachment.type === "file" || attachment.type === "image"
    );
  }

  private resolveFileAttachmentsFromLoadedNoteContent({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): SearchResultFileAttachment[] {
    return this.resolveAttachmentsFromLoadedNoteContent({ noteObservable }).filter(
      (attachment): attachment is SearchResultFileAttachment => attachment.type === "file"
    );
  }

  private resolveImageAttachmentsFromLoadedNoteContent({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): SearchResultImageAttachment[] {
    return this.resolveAttachmentsFromLoadedNoteContent({ noteObservable }).filter(
      (attachment): attachment is SearchResultImageAttachment => attachment.type === "image"
    );
  }

  private resolveMatchingFileAttachment({
    searchAttachmentUrl,
    fileAttachments,
  }: {
    searchAttachmentUrl?: string | null;
    fileAttachments: SearchResultFileAttachment[];
  }): SearchResultFileAttachment | null {
    return resolveMatchingAttachmentByUrl({
      searchAttachmentUrl,
      attachments: fileAttachments,
    });
  }

  private resolveMatchingImageAttachment({
    searchAttachmentUrl,
    imageAttachments,
  }: {
    searchAttachmentUrl?: string | null;
    imageAttachments: SearchResultImageAttachment[];
  }): SearchResultImageAttachment | null {
    return resolveMatchingAttachmentByUrl({
      searchAttachmentUrl,
      attachments: imageAttachments,
    });
  }

  private generateNoteDropdown({
    noteObservable,
  }: {
    noteObservable: INoteObservable;
  }): MDSMenuContentList {
    return [
      {
        id: `share-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Share,
        label: "Share",
        onClick: () =>
          this.shareSheetModal.open({
            id: noteObservable.id,
            entityKind: ShareSheetEntityKind.Note,
            eventContext: EventContext.SearchRowActions,
          }),
      },
      {
        id: `copy-link-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Copy,
        label: "Copy link",
        onClick: () => actions.copyNoteLinkToClipboard({ noteId: noteObservable.id }),
      },
      {
        id: "divider-1",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `favorite-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: noteObservable.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack,
        label: noteObservable.isFavorited ? "Unpin" : "Pin",
        onClick: async () => await noteObservable.toggleFavorite(),
      },
      {
        id: `add-to-collection-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Collection,
        label: "Organize",
        onClick: () =>
          this.addToCollectionModal.open({
            noteIds: [noteObservable.id],
            eventContext: EventContext.SearchRowActions,
          }),
      },
      {
        id: "divider-2",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `move-to-trash-${noteObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Trash,
        label: "Trash",
        onClick: async () => await noteObservable.moveToTrash(),
      },
    ];
  }

  private generateCollectionDropdown({
    collectionObservable,
  }: {
    collectionObservable: CollectionObservable;
  }): MDSMenuContentList {
    return [
      {
        id: `share-${collectionObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Share,
        label: "Share",
        onClick: () =>
          this.shareSheetModal.open({
            id: collectionObservable.id,
            entityKind: ShareSheetEntityKind.Collection,
            eventContext: EventContext.SearchRowActions,
          }),
      },
      {
        id: `copy-link-${collectionObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Copy,
        label: "Copy link",
        onClick: () => {
          actions.copyCollectionLinkToClipboard({ collectionId: collectionObservable.id });
        },
      },
      {
        id: "divider-1",
        kind: MDSMenuContentItemKind.Divider,
      },
      {
        id: `delete-${collectionObservable.id}`,
        kind: MDSMenuContentItemKind.Item,
        icon: MDSIconKind.Trash,
        label: "Delete",
        onClick: () =>
          actions.deleteCollectionPermanently({
            collection: collectionObservable,
            store: this.store,
          }),
      },
    ];
  }

  private async submitClickFeedback(
    trackingId: string,
    pageCursor: string | null,
    searchSessionId: string,
    userSpaceId: string
  ) {
    const response = await api.post("/v2/search/feedback", {
      params: {
        query: {
          space_id: userSpaceId,
        },
      },
      body: {
        search_engine_result_id: trackingId,
        search_session_page_cursor: pageCursor,
        session_id: searchSessionId,
        feedback_kind: "CLICK",
      },
    });

    if (response.error) {
      console.warn("Error in submitting the click feedback: ", response.error);
    }
  }

  private generateItemRows(results: SearchEngineResult[]): ItemListRowData[] {
    const rows: ItemListRowData[] = [];
    let lastHeading = "";

    let notePosition = 0;
    let collectionPosition = 0;
    let carouselPosition = 0;
    const isAdminDebugModeEnabled =
      isMemAccount(this.store.account.myAccount) && this.store.debug.debugModeEnabled;

    const items = this.showDeepSearchResults ? results : results.filter(item => !item.isDeepSearch);

    for (const result of items) {
      switch (result.kind) {
        case "HEADING": {
          const title = (result.value as ApiSchema["SearchEngineResultSectionHeadingValue"]).label;

          // If a following response returns the continuation of the previous heading.
          if (title === lastHeading) break;
          lastHeading = title;
          const row: ItemListSectionHeaderRowData = {
            type: ItemListRowType.SectionHeader,
            payload: {
              title,
            },
            key: `heading-${title}`,
          };
          if (items.indexOf(result) !== items.length - 1) {
            rows.push(row);
          }
          break;
        }
        case "ITEM": {
          const item = result.value as ApiSchema["SearchEngineResultSectionItemValue"];
          switch (item.kind) {
            case "NOTE": {
              const value = item.value as ApiSchema["SearchEngineResultSectionItemNoteValue"];
              const id = value.note_id;
              const searchSessionCursor = value.metadata.search_session_cursor;
              const feedbackApiQuery = result.isDeepSearch ? this.apiQuery : this.fastApiQuery;
              const searchSessionId = feedbackApiQuery.latestSearchSessionData?.searchSessionId;

              const itemAppearance = itemsModule.resolveAppearance({
                itemId: id,
                appStore: this.store,
              });
              const isVisibleDeepSearchResult = this.showDeepSearchResults && result.isDeepSearch;
              const shouldShowDeepSearchAttachmentMatches = isVisibleDeepSearchResult;
              const currentNotePosition = notePosition;
              notePosition++;

              const buildNoteRow = (noteObservable: INoteObservable): ItemListItemRowData => {
                const collectionObservables = noteObservable.collectionList?.allCollections || [];
                const secondaryLabel =
                  noteObservable.suggestionData?.suggested_secondary_label ||
                  noteObservable.secondaryTitle ||
                  "No additional text";
                const deepSearchAttachmentMatches = shouldShowDeepSearchAttachmentMatches
                  ? this.resolveDeepSearchAttachmentMatches({
                      noteValue: value,
                    })
                  : [];
                const deepSearchPdfAttachmentMatches = shouldShowDeepSearchAttachmentMatches
                  ? this.resolveDeepSearchPdfAttachmentMatches({ noteValue: value })
                  : [];
                const deepSearchImageAttachmentMatches = shouldShowDeepSearchAttachmentMatches
                  ? this.resolveDeepSearchImageAttachmentMatches({ noteValue: value })
                  : [];
                const deepSearchPdfChipItems = this.resolveDeepSearchPdfChipItems({
                  attachmentMatches: deepSearchPdfAttachmentMatches,
                  noteObservable,
                  searchEngineResultId: value.metadata.search_engine_result_id,
                });
                const deepSearchImageThumbnailItems = this.resolveDeepSearchImageThumbnailItems({
                  attachmentMatches: deepSearchImageAttachmentMatches,
                  noteObservable,
                });
                const deepSearchAttachmentMediaOrder = resolveSearchResultAttachmentMediaOrder({
                  attachmentMatches: deepSearchAttachmentMatches,
                  pdfMatches: deepSearchPdfAttachmentMatches,
                  imageMatches: deepSearchImageAttachmentMatches,
                });
                const hasDeepSearchAttachmentContent =
                  deepSearchPdfChipItems.length > 0 || deepSearchImageThumbnailItems.length > 0;
                const snippetRows = resolveSearchResultFeaturedSnippet({
                  secondaryLabel,
                  itemValue: value,
                });

                const mdsItem: ItemListItem = {
                  id,
                  appearance: itemAppearance,
                  kind: ItemListItemKind.Note,
                  createPreviewState: () =>
                    new ItemPreviewState({
                      store: this.store,
                      id,
                      kind: ItemListItemKind.Note,
                    }),
                  label: noteObservable.title,
                  rightAlignedComponent: searchSessionId
                    ? isHighlighted =>
                        isHighlighted ? (
                          <SearchResultVotingButtons
                            itemId={id}
                            searchEngineResultId={value.metadata.search_engine_result_id}
                            pageCursor={searchSessionCursor}
                            searchSessionId={searchSessionId}
                            userSpaceId={this.store.spaces.myPersonalSpaceId}
                            adminScore={isAdminDebugModeEnabled ? item.score.value : undefined}
                            additionalElements={
                              value.metadata.source_debug_info && (
                                <SearchResultDebugInfo
                                  rawScoreFromSource={value.metadata.source_debug_info.raw_score}
                                  rerankerScore={value.metadata.source_debug_info.reranker_score}
                                  preAdjustedScore={
                                    value.metadata.source_debug_info.pre_adjusted_score
                                  }
                                  source={value.metadata.source_debug_info.kind}
                                />
                              )
                            }
                          />
                        ) : undefined
                    : undefined,
                  onClick: ({ event }) => {
                    if (searchSessionId) {
                      this.submitClickFeedback(
                        value.metadata.search_engine_result_id,
                        searchSessionCursor,
                        searchSessionId,
                        this.store.spaces.myPersonalSpaceId
                      );
                    } else {
                      console.warn("No search session id found. Feedback buttons not rendered.");
                    }

                    this.store.navigation.goToNote({
                      noteId: id,
                      event,
                      activeTabId: this.tabId,
                    });

                    trackEvent(TrackedEvent.SearchResultsOpenNote, {
                      query: this.filters.searchQuery,
                      sort_by: this.filters.params.sortBy,
                      position: currentNotePosition,
                      note_id: id,
                      note_primary_label: noteObservable.title,
                    });
                  },
                  sharedBy: noteObservable.sharedBy,
                  lastAction: noteObservable.lastAction,
                  titleIcon: isVisibleDeepSearchResult
                    ? {
                        kind: MDSIconKind.Radar,
                        color: MDSIconColor.Emphasis,
                        tooltip: "Deep Search result",
                      }
                    : noteObservable.isShared
                      ? { kind: MDSIconKind.Shared }
                      : undefined,
                  dateLabel: generateShortDateString(
                    this.filters.params.sortBy === SortByKind.LastCreated
                      ? noteObservable.createdAt
                      : noteObservable.modifiedAt
                  ),
                  isSpecial: isVisibleDeepSearchResult,
                  dropdown: this.generateNoteDropdown({ noteObservable }),
                  listState: this.listState,
                  chips: () => {
                    const sortedCollections = sortCollectionsForChips(collectionObservables);
                    if (sortedCollections.length === 0) return null;
                    return <ItemListRowFeaturedContent inline collections={sortedCollections} />;
                  },
                  extraRow: {
                    id: `note-${id}-content`,
                    content: () => (
                      <SearchResultExtraRowContent>
                        {snippetRows.map((snippetRow, index) => (
                          <ItemListRowFeaturedContent
                            key={`note-${id}-snippet-row-${index}`}
                            snippet={snippetRow}
                          />
                        ))}
                        {hasDeepSearchAttachmentContent && (
                          <SearchResultPdfChips
                            chips={deepSearchPdfChipItems}
                            imageThumbnails={deepSearchImageThumbnailItems}
                            mediaOrder={deepSearchAttachmentMediaOrder}
                          />
                        )}
                      </SearchResultExtraRowContent>
                    ),
                  },
                };

                return {
                  type: ItemListRowType.Item,
                  key: id,
                  payload: mdsItem,
                };
              };

              const noteObservable = this.store.notes.get(id);
              if (noteObservable) {
                if (
                  noteObservable.isTrashed ||
                  noteObservable.isDeleted ||
                  !noteObservable.canAccess
                ) {
                  continue;
                }
              }

              rows.push({
                type: ItemListRowType.AsyncNote,
                key: id,
                payload: {
                  noteId: id,
                  itemRow: buildNoteRow,
                },
              });
              break;
            }
            case "COLLECTION": {
              const value = item.value as ApiSchema["SearchEngineResultSectionItemCollectionValue"];
              const id = value.collection_id;
              // TODO: delay until render time.
              const collectionObservable = this.store.collections.getCollectionObservableById({
                collectionId: id,
              });

              if (!collectionObservable) break;

              const itemAppearance = itemsModule.resolveAppearance({
                itemId: id,
                appStore: this.store,
              });

              rows.push({
                type: ItemListRowType.Item,
                key: id,
                payload: {
                  id,
                  kind: ItemListItemKind.Collection,
                  appearance: itemAppearance,
                  createPreviewState: () =>
                    new ItemPreviewState({
                      store: this.store,
                      id: id,
                      kind: ItemListItemKind.Collection,
                    }),
                  label: collectionObservable.label,
                  onClick: ({ event }) => {
                    this.store.navigation.goToCollection({
                      collectionId: id,
                      event,
                      activeTabId: this.tabId,
                    });

                    /**
                     * Track the navigation to a collection
                     */
                    trackEvent(TrackedEvent.SearchResultsOpenCollection, {
                      query: this.filters.searchQuery,
                      sort_by: this.filters.params.sortBy,
                      position: collectionPosition,
                      collection_id: id,
                      collection_primary_label: collectionObservable.label,
                    });
                  },
                  sharedBy: collectionObservable.sharedBy,
                  dateLabel: generateShortDateString(
                    this.filters.params.sortBy === SortByKind.LastCreated
                      ? collectionObservable.createdAt
                      : collectionObservable.modifiedAt
                  ),
                  dropdown: this.generateCollectionDropdown({ collectionObservable }),
                  listState: this.listState,
                },
              });
              collectionPosition++;
              break;
            }
          }
          break;
        }
        case "CAROUSEL": {
          const value = result.value as ApiSchema["SearchEngineResultSectionCarouselValue"];
          const carousel: ItemListCarouselRowData = {
            type: ItemListRowType.Carousel,
            key: `carousel-${rows.length}`,
            payload: {
              items: [],
            },
          };
          for (const item of value.items) {
            switch (item.kind) {
              case "COLLECTION": {
                const { collection_id: collectionId } =
                  item.value as ApiSchema["SearchEngineResultSectionCarouselItemCollectionValue"];
                const collection = this.store.collections.getCollectionObservableById({
                  collectionId,
                });
                if (!collection) break;

                carousel.payload.items.push(
                  getItemListCarouselItemForCollection({
                    collection,
                    store: this.store,
                    position: carouselPosition,
                    query: this.filters.searchQuery,
                    sortBy: this.filters.params.sortBy,
                  })
                );
                carouselPosition++;
                break;
              }
            }
          }
          if (carousel.payload.items.length) {
            rows.push(carousel);
          }
          break;
        }
      }
    }

    if (rows.length && this.notAllItemsLoadedButCould) {
      rows.push({
        type: ItemListRowType.Other,
        key: "reached-capacity",
        payload: {
          children: <SearchPagePaginationCapacityReached />,
        },
      });
    }

    if (rows.length) {
      rows.push({
        type: ItemListRowType.Padding,
        key: "padding",
        payload: { height: 50 },
      });
    }

    return rows;
  }

  async setupIds() {
    const setupGeneration = ++this.itemSetupGeneration;

    runInAction(() => {
      this.isDedupingItems = true;
    });

    const items = selectSearchResultItems({
      fastItems: this.fastApiQuery.items,
      smartItems: this.apiQuery.items,
      smartSearchSucceeded: this.apiQuery.isSuccess,
      showSmartSearchResults: this.showDeepSearchResults,
    });
    const mainList: SearchEngineResult[] = [];
    const carouselItems: SearchEngineResultSectionCarouselItems[] = [];
    const mainListById = new Map<string, SearchEngineResult>();

    const seenIdsInCarousel = new Set<string>();

    // dedupe first + collect unique ids
    for (const item of items) {
      if (item.kind === "CAROUSEL") {
        const value = item.value as SearchEngineResultSectionCarouselValue;
        const dedupedItems = this.dedupeItemsForCarouselValue(value, seenIdsInCarousel);
        carouselItems.push(...dedupedItems);
      } else if (item.kind === "ITEM") {
        const dedupedIds = this.dedupeIdForItemValue(item.value as SearchEngineResultItemValue);
        if (!dedupedIds) continue;

        const existingItem = mainListById.get(dedupedIds);
        if (existingItem) {
          this.mergeDuplicateNoteSearchResultItem({
            existingItem,
            incomingItem: item,
          });
          continue;
        }

        mainListById.set(dedupedIds, item);
        mainList.push(item);
      }
    }

    const listItems: SearchEngineResult[] = [];
    if (!mainList.length && !carouselItems.length) {
      if (setupGeneration !== this.itemSetupGeneration) {
        return;
      }

      runInAction(() => {
        this.items = [];
        this.isDedupingItems = false;
      });

      return;
    }

    // then get sorted collections
    if (seenIdsInCarousel.size) {
      await this.sortAndGroupCollections(Array.from(seenIdsInCarousel), carouselItems, listItems);
    }

    // then get sorted notes
    if (mainListById.size) {
      await this.sortAndGroupNotes(Array.from(mainListById.keys()), mainList, listItems);
    }

    if (setupGeneration !== this.itemSetupGeneration) {
      return;
    }

    runInAction(() => {
      this.items = listItems;
      this.isDedupingItems = false;
    });

    this.prefetchDeepSearchNoteContentDocuments({ items: listItems });

    // Only load more if we haven't reached capacity and queries are not in error state
    const hasErrors =
      this.apiQuery.latestRequest?.isError || this.fastApiQuery.latestRequest?.isError;
    if (!this.reachedCapacity && !hasErrors) {
      this.load();
    }
  }

  private prefetchDeepSearchNoteContentDocuments({ items }: { items: SearchEngineResult[] }) {
    for (const item of items) {
      if (!item.isDeepSearch || item.kind !== "ITEM") {
        continue;
      }

      const itemValue = item.value as SearchEngineResultItemValue;
      if (itemValue.kind !== "NOTE") {
        continue;
      }

      const noteValue = itemValue.value as SearchEngineResultItemNoteValue;
      const noteObservable = this.store.notes.get(noteValue.note_id);
      if (!noteObservable) {
        continue;
      }

      const attachmentMatches = this.resolveDeepSearchAttachmentMatches({ noteValue });
      if (!attachmentMatches.length) {
        continue;
      }

      if (noteObservable.isNoteContentDocumentLoaded) {
        continue;
      }

      if (this.prefetchedDeepSearchNoteContentIds.has(noteObservable.id)) {
        continue;
      }

      this.prefetchedDeepSearchNoteContentIds.add(noteObservable.id);

      void noteObservable.fetchNoteContentDocument().finally(() => {
        this.prefetchedDeepSearchNoteContentIds.delete(noteObservable.id);
      });
    }
  }

  async sortAndGroupNotes(
    ids: string[],
    notesList: SearchEngineResult[],
    listItems: SearchEngineResult[]
  ) {
    if (!notesList.length) return;

    if (this.filters.params.sortBy === SortByKind.Relevance || !this.filters.params.sortBy) {
      listItems.push({ kind: "HEADING", value: { label: "Notes" } });
      for (const item of notesList) {
        listItems.push(item);
      }
      return;
    }

    const noteTuples = await this.store.notes.search({
      lens: LensKind.All,
      sortBy: this.filters.params.sortBy as NotesSortByKind,
      limit: 10000,
      filter: {
        ...this.filters,
        ids,
      },
    });

    if (noteTuples.length) {
      listItems.push({
        kind: "HEADING",
        value: {
          label: "Notes",
        },
      });

      for (const tuple of noteTuples) {
        const item = notesList.find(item => {
          const noteValue = item.value as SearchEngineResultItemValue;
          const noteValueNoteValue = noteValue.value as SearchEngineResultItemNoteValue;

          return noteValueNoteValue.note_id === tuple[2];
        });
        if (!item) continue;
        listItems.push(item);
      }
    }
  }

  async sortAndGroupCollections(
    ids: string[],
    carouselItems: SearchEngineResultSectionCarouselItems[],
    listItems: SearchEngineResult[]
  ) {
    if (!carouselItems.length) return;

    if (this.filters.params.sortBy === SortByKind.Relevance || !this.filters.params.sortBy) {
      // Carousel items don't have a score; preserve the backend's original order
      listItems.push(
        { kind: "HEADING", value: { label: "Collections" } },
        { kind: "CAROUSEL", value: { items: carouselItems } }
      );
      return;
    }

    const collectionTuples = await searchCollections(this.store, {
      sortBy: this.filters.params.sortBy as CollectionSearchSortByKind,
      limit: 10000,
      filter: {
        ...this.filters,
        ids,
      },
      returns: CollectionSearchReturnType.CollectionIndexTuple,
    });

    if (collectionTuples.length) {
      const listCarouselItems = [];
      for (const tuple of collectionTuples) {
        const item = carouselItems.find(item => {
          const collectionValue = item.value as SearchEngineResultItemCollectionValue;
          return collectionValue.collection_id === tuple[2];
        });
        if (!item) continue;
        listCarouselItems.push(item);
      }

      listItems.push(
        {
          kind: "HEADING",
          value: {
            label: "Collections",
          },
        },
        {
          kind: "CAROUSEL",
          value: {
            items: listCarouselItems,
          },
        }
      );
    }
  }

  private mergeDuplicateNoteSearchResultItem({
    existingItem,
    incomingItem,
  }: {
    existingItem: SearchEngineResult;
    incomingItem: SearchEngineResult;
  }) {
    if (existingItem.kind !== "ITEM" || incomingItem.kind !== "ITEM") {
      return;
    }

    const existingItemValue = existingItem.value as SearchEngineResultItemValue;
    const incomingItemValue = incomingItem.value as SearchEngineResultItemValue;

    if (existingItemValue.kind !== "NOTE" || incomingItemValue.kind !== "NOTE") {
      return;
    }

    const existingNoteValue = existingItemValue.value as SearchEngineResultItemNoteValue;
    const incomingNoteValue = incomingItemValue.value as SearchEngineResultItemNoteValue;

    const mergedFeaturedCollectionIds = Array.from(
      new Set([
        ...(existingNoteValue.featured_collection_ids ?? []),
        ...(incomingNoteValue.featured_collection_ids ?? []),
      ])
    );

    const mergedHighlightedSnippets = Array.from(
      new Map(
        [
          ...(existingNoteValue.highlighted_snippets ?? []),
          ...(incomingNoteValue.highlighted_snippets ?? []),
        ].map(snippet => [
          [
            snippet.text,
            snippet.is_highlight,
            snippet.is_truncated_start,
            snippet.is_truncated_end,
          ].join(":"),
          snippet,
        ])
      ).values()
    );

    const mergedAttachmentMatchInfos = Array.from(
      new Map(
        [
          ...(existingNoteValue.attachment_match_infos ?? []),
          ...(incomingNoteValue.attachment_match_infos ?? []),
        ].map(match => [
          [
            match.attachment_id,
            match.attachment_type,
            match.page_number ?? "",
            match.match_source,
            match.sourcer_kind ?? "",
            match.importance ?? "",
          ].join(":"),
          match,
        ])
      ).values()
    );

    existingItemValue.value = {
      ...existingNoteValue,
      ...incomingNoteValue,
      note_id: existingNoteValue.note_id,
      metadata: incomingItem.isDeepSearch ? incomingNoteValue.metadata : existingNoteValue.metadata,
      featured_collection_ids: mergedFeaturedCollectionIds.length
        ? mergedFeaturedCollectionIds
        : undefined,
      highlighted_snippets: mergedHighlightedSnippets.length
        ? mergedHighlightedSnippets
        : undefined,
      attachment_match_infos: mergedAttachmentMatchInfos.length
        ? mergedAttachmentMatchInfos
        : undefined,
    };

    existingItem.isDeepSearch = Boolean(existingItem.isDeepSearch || incomingItem.isDeepSearch);
  }

  get itemRows(): ItemListRowData[] {
    return this.generateItemRows(this.items);
  }

  dedupeIdForItemValue(value: SearchEngineResultItemValue): string | undefined {
    if (value.kind === "NOTE") {
      const noteValue = value.value as SearchEngineResultItemNoteValue;
      return noteValue.note_id;
    }

    return undefined;
  }

  dedupeItemsForCarouselValue(
    value: SearchEngineResultSectionCarouselValue,
    seenIdsInCarousel: Set<string>
  ) {
    const items = value.items as SearchEngineResultSectionCarouselItems[];
    return items.filter(item => {
      let id: string | undefined;
      if (item.kind === "COLLECTION") {
        const collectionValue = item.value as SearchEngineResultItemCollectionValue;
        id = collectionValue.collection_id;
      }

      if (!id || seenIdsInCarousel.has(id)) return false;
      seenIdsInCarousel.add(id);
      return true;
    });
  }

  get orderedItemIds() {
    return this.itemRows.map(row => row.key);
  }

  get hasNoItems() {
    return (
      !this.itemRows.length &&
      !this.fastApiQuery.isLoading &&
      !this.apiQuery.isLoading &&
      !this.isDedupingItems
    );
  }

  // Favorite Search
  get isFavorited() {
    return this.store.favoriteItems.has(this.favoriteItemId);
  }

  get favoriteItemId() {
    return resolveFavoriteItemSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      itemId: this.id,
    });
  }

  get id() {
    return resolveSavedSearchSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      queryString: this.filters.searchQuery,
    });
  }

  toggleFavorite = async () => {
    const savedSearchLabel = this.filters.params.queryString;
    const savedSearchQueryStr = this.filters.searchQuery;

    const existingSavedSearch = this.store.favoriteItems.has(this.favoriteItemId);

    if (existingSavedSearch) {
      await new RemoveSavedSearchFromFavoritesOperation({
        store: this.store,
        payload: { saved_search_id: this.id },
        savedSearchQueryString: savedSearchQueryStr,
      }).execute();
    } else {
      await new AddSavedSearchToFavoritesOperation({
        store: this.store,
        payload: {
          saved_search_query_string: savedSearchQueryStr,
          saved_search_label: savedSearchLabel,
        },
      }).execute();
    }
  };

  resetPreviousQueryString = () => {
    this.previousSearchEngineQueryString = null;
  };
}

type SearchEngineResultItemValue = ApiSchema["SearchEngineResultSectionItemValue"];
type SearchEngineResultItemNoteValue = ApiSchema["SearchEngineResultSectionItemNoteValue"];
type SearchEngineResultItemCollectionValue =
  ApiSchema["SearchEngineResultSectionItemCollectionValue"];
type SearchEngineResultSectionCarouselValue = ApiSchema["SearchEngineResultSectionCarouselValue"];
type SearchEngineResultSectionCarouselItems = ApiSchema["SearchEngineResultSectionCarouselItems"];

interface SearchPagePdfLightboxState {
  src: string;
  fileName: string;
  initialPage?: number;
}

interface SearchPageImageLightboxState {
  images: IImageLightboxImage[];
  initialIndex: number;
}

type SearchPageImageLightboxItem = {
  id: string;
  src: string;
  alt: string;
};

type SearchEngineResultAttachmentMatchInfo = NonNullable<
  SearchEngineResultItemNoteValue["attachment_match_infos"]
>[number];
type SearchEngineResultAttachmentType = SearchEngineResultAttachmentMatchInfo["attachment_type"];
type SearchResultFileAttachment = {
  type: "file";
  id: string | null;
  filename?: string;
  sizeBytes?: number;
  url?: string;
};
type SearchResultImageAttachment = {
  type: "image";
  id: string | null;
  filename?: string;
  name?: string;
  alt?: string;
  url?: string;
};
type SearchResultMediaAttachment = SearchResultFileAttachment | SearchResultImageAttachment;

type SearchResultAttachmentMediaOrderResolverMatch = Pick<
  SearchEngineResultAttachmentMatchInfo,
  "attachment_id" | "attachment_type" | "importance"
>;

export const resolveSearchResultAttachmentMediaOrder = ({
  attachmentMatches,
  pdfMatches,
  imageMatches,
}: {
  attachmentMatches: SearchResultAttachmentMediaOrderResolverMatch[];
  pdfMatches: SearchResultAttachmentMediaOrderResolverMatch[];
  imageMatches: SearchResultAttachmentMediaOrderResolverMatch[];
}): SearchResultAttachmentMediaOrder => {
  const topPdfMatch = pdfMatches[0];
  const topImageMatch = imageMatches[0];

  if (!topPdfMatch && topImageMatch) {
    return SearchResultAttachmentMediaOrder.ImageFirst;
  }

  if (!topImageMatch) {
    return SearchResultAttachmentMediaOrder.PdfFirst;
  }

  // Compare importance to determine if pdf or image should be first
  if (typeof topPdfMatch?.importance === "number" && typeof topImageMatch.importance === "number") {
    return topImageMatch.importance > topPdfMatch.importance
      ? SearchResultAttachmentMediaOrder.ImageFirst
      : SearchResultAttachmentMediaOrder.PdfFirst;
  }

  // If importance is not present, use the backend attachment order
  const topPdfMatchPosition = attachmentMatches.findIndex(
    attachmentMatch =>
      attachmentMatch.attachment_type === "pdf" &&
      attachmentMatch.attachment_id === topPdfMatch?.attachment_id
  );
  const topImageMatchPosition = attachmentMatches.findIndex(
    attachmentMatch =>
      attachmentMatch.attachment_type === "image" &&
      attachmentMatch.attachment_id === topImageMatch.attachment_id
  );

  if (topPdfMatchPosition < 0 || topImageMatchPosition < 0) {
    return SearchResultAttachmentMediaOrder.PdfFirst;
  }

  return topImageMatchPosition < topPdfMatchPosition
    ? SearchResultAttachmentMediaOrder.ImageFirst
    : SearchResultAttachmentMediaOrder.PdfFirst;
};

export type ProxyAttachmentUrlClassification =
  | { kind: "already-proxied"; url: string }
  | { kind: "needs-proxy"; originalUrl: string };

export const classifyProxyAttachmentUrl = ({
  attachmentUrl,
}: {
  attachmentUrl: string;
}): ProxyAttachmentUrlClassification => {
  // If the URL is already a real http(s) proxy URL with a token, keep it as-is — short-circuit
  // before unwrapping, since unwrapping an already-proxied URL would strip the token.
  try {
    const parsed = new URL(attachmentUrl);
    const isHttpProxyUrl =
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      parsed.pathname.toLowerCase().endsWith("/proxy") &&
      parsed.searchParams.has("url");

    if (isHttpProxyUrl) {
      return { kind: "already-proxied", url: attachmentUrl };
    }
  } catch {
    // Fall through — URL parsing failed, treat as needing a proxy wrap.
  }

  // Common-editor file nodes store downloadUrl with a synthetic `mem-storage://proxy?url=<real>`
  // scheme. Unwrap it so we re-proxy against the real asset URL, matching the editor's render
  // pipeline (see apply-file-proxy-to-html-attributes in common-editor).
  const unwrapped =
    assetProxyModule.extractOriginalUrlFromProxy({ proxiedUrl: attachmentUrl }) ?? attachmentUrl;

  return { kind: "needs-proxy", originalUrl: unwrapped };
};
